import * as React from 'react'
import styled from 'styled-components'
import {
  ConfirmAction,
  FlowElement,
  NewButton,
  NotificationType,
  SvgIcon,
  Title,
  truncateFilename,
} from 'prace-common-components'
import { useAppDispatch } from 'store/hooks'
import {
  useGetElementDocumentsQuery,
  useCreateDocumentsMutation,
  useAddDocumentVersionMutation,
  useDeleteDocumentMutation,
  useLazyGetDocumentVersionUrlQuery,
} from 'store/api/calls/documents'
import { FlowDocument, FlowDocumentVersion } from 'store/api/calls/documents/types'

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.webp'

const $Container = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
`

const $DocumentCard = styled.div`
  margin: ${({ theme }) => theme.spacing(1)} 0;
  padding: ${({ theme }) => theme.spacing(1)};
  border: 1px solid ${({ theme }) => theme.colors.greyScales[300]};
  border-radius: 4px;
`

const $Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
`

const $Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  flex-shrink: 0;
`

const $DocTitle = styled.a`
  flex: 1 1 auto;
  min-width: 0;
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const $VersionList = styled.ul`
  list-style: none;
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding: 0;
`

const $VersionItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1)};
  margin: ${({ theme }) => theme.spacing(0.5)} 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.greyScales[800]};
`

const $Link = styled.a`
  color: ${({ theme }) => theme.colors.primary.main};
  cursor: pointer;
  text-decoration: underline;
`

const $Empty = styled.p`
  color: ${({ theme }) => theme.colors.greyScales[800]};
`

const $HiddenInput = styled.input`
  display: none;
`

const latestVersion = (doc: FlowDocument): FlowDocumentVersion | undefined =>
  doc.versions?.length ? doc.versions[doc.versions.length - 1] : undefined

export const DocumentPreviewManager: React.FC<{ elementId: FlowElement['id'] }> = ({ elementId }) => {
  const dispatch = useAppDispatch()
  const { data: documents, refetch } = useGetElementDocumentsQuery(elementId)
  const [createDocuments] = useCreateDocumentsMutation()
  const [addDocumentVersion] = useAddDocumentVersionMutation()
  const [deleteDocument] = useDeleteDocumentMutation()
  const [getVersionUrl] = useLazyGetDocumentVersionUrlQuery()

  const addInputRef = React.useRef<HTMLInputElement>(null)
  const replaceInputRefs = React.useRef<Record<number, HTMLInputElement | null>>({})

  const notify = (type: NotificationType, msg: string) =>
    dispatch({ type: 'notification', payload: { type, msg } })

  const handleAddFiles = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : []
    event.target.value = ''
    if (!files.length) return
    try {
      await createDocuments({ flowElementId: elementId, files }).unwrap()
      notify(NotificationType.success, 'Document(s) added')
      refetch()
    } catch {
      notify(NotificationType.error, 'Failed to add document(s)')
    }
  }

  const handleReplace = async (documentId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    try {
      await addDocumentVersion({ id: documentId, file }).unwrap()
      notify(NotificationType.success, 'New version uploaded')
      refetch()
    } catch {
      notify(NotificationType.error, 'Failed to upload new version')
    }
  }

  const handleDelete = async (documentId: number) => {
    try {
      await deleteDocument(documentId).unwrap()
      notify(NotificationType.success, 'Document removed')
      refetch()
    } catch {
      notify(NotificationType.error, 'Failed to remove document')
    }
  }

  const handleView = async (versionId: number) => {
    /* Open the tab synchronously to avoid the popup blocker, then point it at the presigned URL */
    const tab = window.open('', '_blank')
    try {
      const { url } = await getVersionUrl(versionId).unwrap()
      if (tab) tab.location.href = url
    } catch {
      if (tab) tab.close()
      notify(NotificationType.error, 'Failed to open document')
    }
  }

  return (
    <$Container>
      <Title noWrap>Documents</Title>
      {documents?.length ? documents.map((doc) => {
        const current = latestVersion(doc)
        return (
          <$DocumentCard key={doc.id}>
            <$Row>
              <$DocTitle
                title={current?.originalFilename}
                onClick={() => current && handleView(current.id)}
              >
                {current?.originalFilename ? truncateFilename(current.originalFilename) : 'Document'}
              </$DocTitle>
              <$Actions>
                <NewButton
                  variant='outlined'
                  onClick={() => replaceInputRefs.current[doc.id]?.click()}
                >
                  Replace
                </NewButton>
                <ConfirmAction
                  deletion
                  title='Remove document'
                  description='The document will be hidden, but its versions are kept for past applications.'
                  onConfirm={() => handleDelete(doc.id)}
                >
                  <SvgIcon name='delete' size={18} color='#757778' clickable />
                </ConfirmAction>
              </$Actions>
            </$Row>
            <$HiddenInput
              ref={(el) => { replaceInputRefs.current[doc.id] = el }}
              type='file'
              accept={ACCEPTED_TYPES}
              onChange={(e) => handleReplace(doc.id, e)}
            />
            <$VersionList>
              {(doc.versions || [])
                .map((version, idx) => ({ version, number: idx + 1 }))
                .reverse()
                .map(({ version, number }) => (
                  <$VersionItem key={version.id}>
                    <$Link onClick={() => handleView(version.id)}>Version {number}</$Link>
                  </$VersionItem>
                ))}
            </$VersionList>
          </$DocumentCard>
        )
      }) : (
        <$Empty>No documents uploaded yet.</$Empty>
      )}
      <$HiddenInput
        ref={addInputRef}
        type='file'
        accept={ACCEPTED_TYPES}
        multiple
        onChange={handleAddFiles}
      />
      <NewButton variant='outlined' onClick={() => addInputRef.current?.click()}>
        Upload document(s)
      </NewButton>
    </$Container>
  )
}
